/**
 * Tour Ordering Logic
 *
 * Processes AI-generated tour steps by filtering to connected workflow nodes
 * and applying position-based ordering for consistent spatial flow.
 */

import { Node, Edge } from "@xyflow/react";
import { getAbsoluteNodePosition } from "./getAbsoluteNodePosition";
import { isTourableNode } from "./isTourableNode";

export interface TourStep {
  parentId: string;
  header: string;
  description: string;
  stepNumber: number;
}

export const NODE_SORTING_THRESHOLD = 100;
export const GROUP_SORTING_THRESHOLD = 500;

/**
 * Returns node IDs that participate in the workflow (have edges).
 * Excludes standalone nodes like isolated reference tables or documentation.
 */
export function getConnectedNodeIds(edges: Edge[]): Set<string> {
  return new Set([
    ...edges.map((edge) => edge.source),
    ...edges.map((edge) => edge.target),
  ]);
}

/**
 * Creates position-based ordering that prioritizes main workflow and keeps grouped nodes together.
 * Filters out visual elements and unconnected nodes, then sorts by spatial position.
 */
export function createGroupAwareOrder(
  nodes: Node[],
  edges: Edge[],
  nodeMap: Map<string, Node>,
) {
  const connectedNodeIds = getConnectedNodeIds(edges);

  // Filter to workflow nodes only (exclude visual elements and unconnected nodes)
  // If no edges exist, include all tourable nodes (handles standalone node canvases)
  const connectedNodes =
    edges.length === 0
      ? nodes.filter(isTourableNode)
      : nodes
          .filter(isTourableNode)
          .filter((node) => connectedNodeIds.has(node.id));

  // Group nodes by parent (null = main workflow, string = grouped nodes)
  const nodesByGroup = new Map<string | null, Node[]>();

  connectedNodes.forEach((node) => {
    const groupId = node.parentId || null;
    if (!nodesByGroup.has(groupId)) {
      nodesByGroup.set(groupId, []);
    }
    nodesByGroup.get(groupId)!.push(node);
  });

  // Position-based sorting: left-to-right, then top-to-bottom
  const sortByPosition = (
    itemList: Node[],
    threshold: number,
    allNodes: Node[],
  ) =>
    itemList.sort((a, b) => {
      const posA = getAbsoluteNodePosition(a, allNodes);
      const posB = getAbsoluteNodePosition(b, allNodes);
      const xDiff = posA.x - posB.x;
      return Math.abs(xDiff) > threshold ? xDiff : posA.y - posB.y;
    });

  // Remove empty main workflow group if it exists
  if (nodesByGroup.get(null)?.length === 0) {
    nodesByGroup.delete(null);
  }

  const allNodes = Array.from(nodeMap.values());

  // Sort nodes within groups, then sort groups by position (main workflow first)
  const sortedGroups = Array.from(nodesByGroup.entries())
    .map(([groupId, groupNodes]) => {
      const groupNode = groupId ? nodeMap.get(groupId) : undefined;
      const groupPosition =
        groupId === null
          ? getAbsoluteNodePosition(groupNodes[0], allNodes)
          : groupNode?.position;

      return {
        groupId,
        nodes: sortByPosition(
          [...groupNodes],
          NODE_SORTING_THRESHOLD,
          allNodes,
        ),
        position: groupPosition,
      };
    })
    .sort((a, b) => {
      // Main workflow (null groupId) always comes first
      if (a.groupId === null) return -1;
      if (b.groupId === null) return 1;

      const aX = a.position?.x || 0;
      const bX = b.position?.x || 0;
      const aY = a.position?.y || 0;
      const bY = b.position?.y || 0;

      // Large X difference = different columns, sort left-to-right
      const xDiff = Math.abs(aX - bX);
      if (xDiff > GROUP_SORTING_THRESHOLD) {
        return aX - bX;
      }

      // Same column, sort top-to-bottom
      return aY - bY;
    });

  // Flatten groups into ordered list with indices
  const orderedNodes = sortedGroups.flatMap((group) => group.nodes);
  return orderedNodes.map((node, index) => ({ nodeId: node.id, order: index }));
}

/**
 * Reorders tour steps based on position order and renumbers them sequentially.
 */
export function reorderTourSteps(
  tourSteps: TourStep[],
  positionOrder: { nodeId: string; order: number }[],
): TourStep[] {
  const orderMap = new Map(
    positionOrder.map(({ nodeId, order }) => [nodeId, order]),
  );

  return tourSteps
    .sort((a, b) => {
      const orderA = orderMap.get(a.parentId) ?? 999;
      const orderB = orderMap.get(b.parentId) ?? 999;
      return orderA - orderB;
    })
    .map((step, index) => ({ ...step, stepNumber: index + 1 }));
}

/**
 * Main processing function: filters AI tour steps to connected nodes and applies position-based ordering.
 * If no edges exist, includes all tourable nodes to handle standalone node canvases.
 */
export function processTourSteps(
  tourSteps: TourStep[],
  nodes: Node[],
  edges: Edge[],
): TourStep[] {
  const connectedNodeIds = getConnectedNodeIds(edges);

  // If no edges exist, include all tourable nodes (handles standalone node canvases)
  const validTourSteps =
    edges.length === 0
      ? tourSteps.filter((step) => {
          const node = nodes.find((n) => n.id === step.parentId);
          return node && isTourableNode(node);
        })
      : tourSteps.filter((step) => connectedNodeIds.has(step.parentId));

  // Remove duplicate tour steps with same parentId (keep first occurrence)
  // This prevents the LLM from generating multiple tour steps for the same node
  const uniqueTourSteps = validTourSteps.filter(
    (step, index, array) =>
      array.findIndex((s) => s.parentId === step.parentId) === index,
  );

  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const positionOrder = createGroupAwareOrder(nodes, edges, nodeMap);

  return reorderTourSteps(uniqueTourSteps, positionOrder);
}
